import React, { useState, useEffect, useContext, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    makeStyles
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import MovieOutlinedIcon from "@material-ui/icons/MovieOutlined";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const backendUrl = () =>
    (process.env.REACT_APP_BACKEND_URL || "http://localhost:8080").replace(/\/$/, "");

const emptyAsset = {
    name: "",
    label: "",
    type: "link",
    url: "",
    caption: "",
    keywords: "",
    queueId: "",
    promptId: "",
    active: true,
    mediaPath: ""
};

const useStyles = makeStyles((theme) => ({
    btnWrapper: { position: "relative" },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12
    },
    dropZone: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius * 2,
        padding: theme.spacing(2),
        textAlign: "center",
        cursor: "pointer",
        transition: "border-color 0.2s, background-color 0.2s",
        backgroundColor: theme.palette.action.hover,
        "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.selected
        }
    },
    dropZoneActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.selected
    },
    dropIcon: {
        fontSize: 40,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1)
    },
    previewBox: {
        marginTop: theme.spacing(1.5),
        borderRadius: theme.shape.borderRadius * 2,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper
    },
    previewImage: {
        display: "block",
        width: "100%",
        maxHeight: 220,
        objectFit: "contain",
        backgroundColor: "#f5f5f5"
    },
    previewVideo: {
        display: "block",
        width: "100%",
        maxHeight: 220,
        backgroundColor: "#000"
    },
    fileMeta: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        padding: theme.spacing(2)
    },
    hiddenInput: {
        display: "none"
    }
}));

const AssetSchema = Yup.object().shape({
    label: Yup.string().required("Obrigatório"),
    type: Yup.string().required("Obrigatório")
});

const typeOptions = [
    { value: "link", label: "Link (URL)" },
    { value: "image", label: "Imagem" },
    { value: "video", label: "Vídeo" },
    { value: "pdf", label: "PDF" },
    { value: "document", label: "Documento" }
];

const acceptByType = {
    image: "image/*",
    video: "video/*",
    pdf: ".pdf,application/pdf",
    document: ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
};

const isImageType = (type) => type === "image" || type === "imagem";
const isVideoType = (type) => type === "video" || type === "vídeo";

const buildStoredMediaUrl = (mediaPath, companyId) => {
    if (!mediaPath || !companyId) return null;
    const fileName = String(mediaPath).split("/").pop();
    return `${backendUrl()}/public/company${companyId}/ai-assets/${encodeURIComponent(fileName)}`;
};

const AiAssetModal = ({ open, onClose, assetId, reload }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const fileInputRef = useRef(null);
    const blobUrlRef = useRef(null);
    const loadedKeyRef = useRef(null);

    const [asset, setAsset] = useState(emptyAsset);
    const [queues, setQueues] = useState([]);
    const [prompts, setPrompts] = useState([]);
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [localPreviewUrl, setLocalPreviewUrl] = useState(null);

    const clearBlobPreview = () => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
        setLocalPreviewUrl(null);
    };

    const clearFileSelection = () => {
        clearBlobPreview();
        setFile(null);
        setDragActive(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const applySelectedFile = (selectedFile) => {
        if (!selectedFile) return;
        clearBlobPreview();
        const blobUrl = URL.createObjectURL(selectedFile);
        blobUrlRef.current = blobUrl;
        setLocalPreviewUrl(blobUrl);
        setFile(selectedFile);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    useEffect(() => () => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
        }
    }, []);

    useEffect(() => {
        if (!open) {
            loadedKeyRef.current = null;
            return;
        }
        api.get("/queue").then(({ data }) => setQueues(Array.isArray(data) ? data : [])).catch(() => setQueues([]));
        api.get("/prompt").then(({ data }) => setPrompts(Array.isArray(data?.prompts) ? data.prompts : (Array.isArray(data) ? data : []))).catch(() => setPrompts([]));
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const loadKey = assetId ? `edit-${assetId}` : "new";
        if (loadedKeyRef.current === loadKey) return;
        loadedKeyRef.current = loadKey;

        clearFileSelection();

        if (!assetId) {
            setAsset(emptyAsset);
            return;
        }

        const load = async () => {
            try {
                const { data } = await api.get("/ai-assets");
                const found = (data || []).find((a) => a.id === assetId);
                if (found) {
                    setAsset({
                        ...found,
                        keywords: Array.isArray(found.keywords) ? found.keywords.join(", ") : "",
                        queueId: found.queueId || "",
                        promptId: found.promptId || ""
                    });
                }
            } catch (err) {
                toastError(err);
            }
        };
        load();
    }, [assetId, open]);

    const handleSave = async (values) => {
        if (values.type !== "link" && !file && !values.mediaPath && !values.url) {
            toast.error("Envie um arquivo ou informe uma URL alternativa.");
            return;
        }

        try {
            const formData = new FormData();
            if (values.label) formData.append("label", values.label);
            if (values.name) formData.append("name", values.name);
            formData.append("type", values.type);
            if (values.url) formData.append("url", values.url);
            if (values.caption) formData.append("caption", values.caption);
            if (values.keywords) formData.append("keywords", values.keywords);
            formData.append("queueId", values.queueId ? String(values.queueId) : "");
            formData.append("promptId", values.promptId ? String(values.promptId) : "");
            formData.append("active", values.active !== false);
            if (file) formData.append("file", file);

            if (assetId) {
                await api.put(`/ai-assets/${assetId}`, formData);
            } else {
                await api.post("/ai-assets", formData);
            }
            toast.success("Asset salvo com sucesso");
            reload();
            onClose();
        } catch (err) {
            toastError(err);
        }
    };

    const handleDrag = (e, active) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(active);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const dropped = e.dataTransfer?.files?.[0];
        if (dropped) applySelectedFile(dropped);
    };

    const renderMediaPreview = (values) => {
        const storedUrl = buildStoredMediaUrl(values.mediaPath, user?.companyId);
        const previewUrl = localPreviewUrl || storedUrl || (isImageType(values.type) ? values.url : null);
        const videoUrl = localPreviewUrl || storedUrl || (isVideoType(values.type) ? values.url : null);
        const displayName = file?.name || values.mediaPath || values.url || "Arquivo";

        if (isImageType(values.type) && previewUrl) {
            return (
                <div className={classes.previewBox}>
                    <img src={previewUrl} alt={values.label || "Preview"} className={classes.previewImage} />
                </div>
            );
        }

        if (isVideoType(values.type) && videoUrl) {
            return (
                <div className={classes.previewBox}>
                    <video key={videoUrl} src={videoUrl} controls className={classes.previewVideo} />
                </div>
            );
        }

        if ((values.type === "pdf" || values.type === "document") && (storedUrl || file)) {
            return (
                <div className={classes.previewBox}>
                    <div className={classes.fileMeta}>
                        <InsertDriveFileOutlinedIcon color="action" />
                        <div>
                            <Typography variant="body2">{displayName}</Typography>
                            {storedUrl && (
                                <Typography variant="caption" color="textSecondary">
                                    <a href={storedUrl} target="_blank" rel="noopener noreferrer">Abrir arquivo</a>
                                </Typography>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle>{assetId ? "Editar asset de IA" : "Novo asset de IA"}</DialogTitle>
            <Formik
                initialValues={asset}
                enableReinitialize
                validationSchema={AssetSchema}
                onSubmit={(values, actions) => {
                    handleSave(values);
                    actions.setSubmitting(false);
                }}
            >
                {({ values, setFieldValue, isSubmitting }) => (
                    <Form>
                        <DialogContent dividers>
                            <Field
                                as={TextField}
                                label="Rótulo (exibido na lista)"
                                name="label"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                required
                            />
                            <Field
                                as={TextField}
                                label="Nome interno (slug, opcional)"
                                name="name"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                helperText="Se vazio, será gerado a partir do rótulo. A IA usa este nome nas ações."
                            />
                            <FormControl fullWidth margin="dense" variant="outlined">
                                <InputLabel>Tipo</InputLabel>
                                <Field as={Select} name="type" label="Tipo">
                                    {typeOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Field>
                            </FormControl>
                            {values.type === "link" ? (
                                <Field
                                    as={TextField}
                                    label="URL (https://)"
                                    name="url"
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    required
                                />
                            ) : (
                                <>
                                    {renderMediaPreview(values)}
                                    <div
                                        className={`${classes.dropZone} ${dragActive ? classes.dropZoneActive : ""}`}
                                        onClick={() => fileInputRef.current?.click()}
                                        onDragEnter={(e) => handleDrag(e, true)}
                                        onDragOver={(e) => handleDrag(e, true)}
                                        onDragLeave={(e) => handleDrag(e, false)}
                                        onDrop={handleDrop}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
                                        }}
                                    >
                                        {isVideoType(values.type) ? (
                                            <MovieOutlinedIcon className={classes.dropIcon} />
                                        ) : (
                                            <CloudUploadOutlinedIcon className={classes.dropIcon} />
                                        )}
                                        <Typography variant="body2" color="textPrimary">
                                            Arraste a mídia aqui ou clique para selecionar
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {file ? `Selecionado: ${file.name}` : values.mediaPath ? `Atual: ${values.mediaPath}` : "Nenhum arquivo escolhido"}
                                        </Typography>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className={classes.hiddenInput}
                                        accept={acceptByType[values.type] || "image/*,video/*,.pdf,.doc,.docx"}
                                        onChange={(e) => {
                                            applySelectedFile(e.target.files?.[0] || null);
                                        }}
                                    />
                                    <Field
                                        as={TextField}
                                        label="URL alternativa (opcional)"
                                        name="url"
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                </>
                            )}
                            <Field
                                as={TextField}
                                label="Legenda (opcional)"
                                name="caption"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />
                            <Field
                                as={TextField}
                                label="Palavras-chave (separadas por vírgula)"
                                name="keywords"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />
                            <FormControl fullWidth margin="dense" variant="outlined">
                                <InputLabel>Escopo: Fila (opcional)</InputLabel>
                                <Select
                                    value={values.queueId || ""}
                                    onChange={(e) => setFieldValue("queueId", e.target.value)}
                                    label="Escopo: Fila (opcional)"
                                >
                                    <MenuItem value="">Empresa (global)</MenuItem>
                                    {queues.map((q) => (
                                        <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth margin="dense" variant="outlined">
                                <InputLabel>Escopo: Prompt (opcional)</InputLabel>
                                <Select
                                    value={values.promptId || ""}
                                    onChange={(e) => setFieldValue("promptId", e.target.value)}
                                    label="Escopo: Prompt (opcional)"
                                >
                                    <MenuItem value="">Nenhum</MenuItem>
                                    {prompts.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} color="secondary" variant="outlined">Cancelar</Button>
                            <Button type="submit" color="primary" variant="contained" disabled={isSubmitting} className={classes.btnWrapper}>
                                Salvar
                                {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default AiAssetModal;
